import type { fr } from './fr.js';

/** Dictionnaire anglais. Doit couvrir toutes les clés du français (vérifié par le type). */
export const en: Record<keyof typeof fr, string> = {
  'common.loading': 'Loading…',
  'common.error': 'Error',

  'nav.open': 'Open navigation',
  'nav.close': 'Close navigation',
  'nav.expandSection': 'Expand section',
  'nav.collapseSection': 'Collapse section',

  'search.label': 'Search',
  'search.placeholder': 'Search projects, videos, models…',
  'search.clear': 'Clear search',
  'search.searching': 'Searching…',
  'search.empty': 'No results.',
  'search.group.videos': 'Videos',
  'search.group.projects': 'Projects',
  'search.group.missions': 'Flight sessions',
  'search.group.models': 'Models',
  'search.group.libraryModels': 'Model library',

  'assistant.title': 'AI assistant',
  'assistant.close': 'Close assistant',
  'assistant.intro': 'Ask a question about your projects, videos or models.',
  'assistant.placeholder': 'Your question…',
  'assistant.disabledPlaceholder': 'Assistant unavailable',
  'assistant.inputLabel': 'Question for the assistant',
  'assistant.send': 'Send',
  'assistant.thinking': 'The assistant is thinking…',
  'assistant.attach': 'Attach a video',
  'assistant.attaching': 'Uploading…',
  'assistant.attachRemove': 'Remove attachment',
  'assistant.dropHint': 'Drop a video to attach it',
  'assistant.adopt': 'Adopt',
  'assistant.adopting': 'Adopting…',
  'assistant.adopted': 'Adopted',
  'assistant.adoptFailed': 'Adoption failed',
  'assistant.adoptNeedsEditor': 'Editor role required to adopt.',
  'assistant.soonTitle': 'Coming soon',
  'assistant.soonBody':
    'The assistant will be available once the API key is configured on the server (ADR-0016).',

  'account.assistant': 'AI assistant',
  'account.notifications': 'Notifications (coming soon)',
  'account.profile': 'Profile',
  'account.settings': 'Settings',
  'account.logout': 'Log out',
  'account.themeToLight': 'Switch to day theme',
  'account.themeToDark': 'Switch to night theme',
  'language.label': 'Language',
  'language.french': 'French',
  'language.english': 'English',
  'language.ukrainian': 'Ukrainian',

  'overview.title': 'Overview',
  'overview.intro': 'A bird’s-eye view of your organisation and access to the four main features.',
  'overview.tile.projects': 'Projects',
  'overview.tile.campaigns': 'Campaigns',
  'overview.projects.title': 'Projects',
  'overview.projects.new': 'New project',
  'overview.projects.manage': 'Manage in Data →',
  'overview.projects.empty': 'No projects yet. Group videos, images and models into a project.',
  'overview.projects.videos': 'videos',
  'overview.projects.rename': 'Rename',
  'overview.projects.delete': 'Delete',
  'overview.projects.deleteTitle': 'Delete project',
  'overview.projects.deleteBody':
    'The project is deleted, but the associated videos, images and models stay intact.',
  'overview.projects.formName': 'Project name',
  'overview.projects.formDesc': 'Description (optional)',
  'overview.projects.namePlaceholder': 'e.g. Follower drone',
  'overview.projects.save': 'Save',
  'overview.projects.cancel': 'Cancel',
  'overview.tile.sessions': 'Flight sessions',
  'overview.tile.videos': 'Videos',
  'overview.tile.images': 'Images',
  'overview.tile.models': 'Models',
  'overview.recentVideos': 'Latest videos',
  'overview.evaluatedModels': 'Evaluated models',
  'overview.noVideos': 'No videos yet.',
  'overview.noVideosCta': 'Add some in Data collection.',
  'overview.noEval': 'No evaluations yet.',
  'overview.noEvalCta': 'Evaluate a model in Model.',
  'overview.seeAll': 'See all',
  'overview.compare': 'Compare',

  'overview.guide.lead.new': 'Your chain, from the field to the hardware.',
  'overview.guide.lead.active': 'Pick up where you left off, or start a new action.',
  'overview.guide.workflow.title': 'Create a complete workflow',
  'overview.guide.workflow.desc':
    'Chain the four steps into a guided path, from data to deployment.',
  'overview.guide.workflow.cta': 'Start',
  'overview.guide.step.data': 'Data',
  'overview.guide.step.data.desc': 'Videos, simulation',
  'overview.guide.step.annotation': 'Annotation',
  'overview.guide.step.annotation.desc': 'Label, pre-annotate',
  'overview.guide.step.model': 'Model',
  'overview.guide.step.model.desc': 'Import, adopt, evaluate',
  'overview.guide.step.deploy': 'Deployment',
  'overview.guide.step.deploy.desc': 'Package for the target',
  'overview.guide.state.todo': 'To do',
  'overview.guide.state.soon': 'Coming soon',
  'overview.guide.cmd.data': 'Add data',
  'overview.guide.cmd.model': 'Create / update a model',
  'overview.guide.cmd.deploy': 'Deploy',
  'overview.guide.resume.label': 'Resume',
  'overview.guide.resume.toAnnotate': 'to annotate',
  'overview.guide.resume.open': 'open',

  'workflow.title': 'Guided journey',
  'workflow.intro':
    'From data to a deployed model, one step at a time. Your progress is read from your data — nothing to tick off by hand.',
  'workflow.progress': 'steps done',
  'workflow.revisit': 'Revisit',
  'workflow.state.done': 'Done',
  'workflow.state.current': 'Do this now',
  'workflow.state.todo': 'Upcoming',
  'workflow.state.locked': 'Soon',
  'workflow.count.videos': 'videos',
  'workflow.count.annotated': 'annotated',
  'workflow.count.models': 'models',
  'workflow.count.evaluated': 'evaluated',
  'workflow.allDone.title': 'Journey complete',
  'workflow.allDone.desc':
    'Your data has been through the whole available chain. Deployment to hardware is coming soon.',
  'workflow.step.data.title': 'Add data',
  'workflow.step.data.body':
    'Upload your drone videos and their telemetry. They are transcoded, then ready to annotate.',
  'workflow.step.data.action': 'Add data',
  'workflow.step.annotation.title': 'Annotate',
  'workflow.step.annotation.body':
    'Label the objects of interest with bounding boxes, or let pre-annotation get ahead of you.',
  'workflow.step.annotation.action': 'Annotate videos',
  'workflow.step.model.title': 'Get a model',
  'workflow.step.model.body':
    'Import an .onnx model, or adopt one from the library — ready to evaluate on your data.',
  'workflow.step.model.action': 'Create or adopt a model',
  'workflow.step.evaluation.title': 'Evaluate',
  'workflow.step.evaluation.body':
    'Measure a model against your annotated videos (mAP, precision, recall) to know what it is really worth.',
  'workflow.step.evaluation.action': 'Evaluate a model',
  'workflow.step.deploy.title': 'Deploy to hardware',
  'workflow.step.deploy.body':
    'Package a validated model for the target runtime (ONNX, TFLite, TensorRT). Coming soon.',
  'workflow.step.deploy.action': 'Deploy',

  'deploy.title': 'Deploy',
  'deploy.intro':
    'Deploy a validated model to the hardware of your choice (drone, UGV…) and adapt it to its constraints.',
  'deploy.soonBadge': 'Coming soon (ADR-0014, Phase D).',
  'deploy.soonLead':
    'This section will package a model to the target runtime format (ONNX, TFLite, TensorRT), quantise it if needed, and deploy it to a device picked from a list.',
  'deploy.families': 'Two families of characteristics, kept separate by design:',
  'deploy.computeLabel': 'Compute and sensor',
  'deploy.computeText':
    'SoC/accelerator, latency, power, camera resolution/field of view. This is what actually adapts the model: choosing its size and quantisation to fit the on-board budget.',
  'deploy.airframeLabel': 'Airframe',
  'deploy.airframeText':
    'weight, flight dynamics, motors. Kept as fleet metadata (inventory, endurance, planning); these do not parameterise the vision model.',

  'model.error.load': 'Unable to load',
  'model.error.historyUnavailable': 'History unavailable',
  'model.error.evaluate': 'Evaluation failed',
  'model.error.required': 'Name, version and .onnx file are required.',
  'model.error.tooLarge': 'File too large (2 GB maximum).',
  'model.error.uploadRefused': 'Artifact upload refused by storage',
  'model.error.import': 'Import failed',
  'model.error.delete': 'Deletion failed',
  'model.loading': 'Loading…',
  'model.title': 'Models',
  'model.intro':
    'Import your detection models (ONNX) to evaluate them on your annotated data and, eventually, train and deploy them on your hardware (ADR-0014).',
  'model.notif.errorTitle': 'Error',
  'model.notif.actionErrorTitle': 'Action failed',
  'model.import.title': 'Build a model',
  'model.field.name': 'Name',
  'model.field.namePlaceholder': 'e.g. yolov8n',
  'model.field.version': 'Version',
  'model.field.versionPlaceholder': 'e.g. 1',
  'model.field.inputSize': 'Input size',
  'model.field.classes': 'Classes (one per line, in the order of the model’s indices)',
  'model.field.classesHelper': 'Empty = default COCO classes of a generic YOLO.',
  'model.field.description': 'Description (optional)',
  'model.field.artifact': '.onnx artifact',
  'model.import.busy': 'Importing…',
  'model.import.submit': 'Import the model',
  'model.compare.title': 'Comparison',
  'model.compare.evaluatedSuffix': 'evaluated',
  'model.compare.col.model': 'Model',
  'model.compare.col.precision': 'Precision',
  'model.compare.col.recall': 'Recall',
  'model.compare.col.images': 'Images',
  'model.compare.hint':
    'Ranked by descending mAP. The comparison is only valid if the models were evaluated on the same set — check the video in each one’s history.',
  'model.registry.title': 'Registry',
  'model.zoo.mine': 'Your models',
  'model.zoo.catalog': 'Pre-trained catalog',
  'model.zoo.catalogHint': 'Already-trained models (Yolo, ResNet…), ready to adopt.',
  'model.registry.empty': 'No models registered yet.',
  'model.status.ready': 'Ready',
  'model.status.pending': 'Pending',
  'model.status.failed': 'Failed',
  'model.classesCountSuffix': 'class(es)',
  'model.source.trained': 'trained',
  'model.source.imported': 'imported',
  'model.byPrefix': 'by',
  'model.action.evaluate': 'Evaluate',
  'model.action.hide': 'Hide',
  'model.action.history': 'History',
  'model.action.delete': 'Delete',
  'model.eval.headingPrefix': 'Evaluate',
  'model.eval.busy': 'Launching…',
  'model.eval.submit': 'Launch evaluation',
  'model.eval.cancel': 'Cancel',
  'model.eval.intro':
    'The evaluation compares the model’s predictions against the video’s validated annotations, on the worker. The metrics (mAP, precision, recall) will appear on the model once the run is finished.',
  'model.eval.videoLabel': 'Video (validation set)',
  'model.history.loading': 'Loading history…',
  'model.history.empty': 'No evaluations for this model.',
  'model.history.status.pending': 'pending',
  'model.history.status.running': 'running',
  'model.history.status.done': 'done',
  'model.history.status.failed': 'failed',
  'model.history.videoFallback': 'video',
  'model.history.col.class': 'Class',
  'model.history.col.gt': 'GT',
  'model.history.col.confusion': 'TP/FP/FN',

  // Section navigation for the Model page (ADR-0017).
  'model.section.modelZoo': 'Model Zoo',
  'model.section.build': 'Build a model',
  'model.section.objectives': 'Objectives',
  'model.objectives.hint':
    'Each project and its objective — progress is read from your data. Open a project to define or edit its objective.',
  'model.objectives.empty':
    'No projects yet. Create one from the Overview, then define its objective.',
  'model.objectives.none': 'No objective — open the project to define one.',
  'model.objectives.resume': 'Resume',
  'model.objectives.completed': 'Complete',
  'model.objectives.ongoing': 'Objectives in progress',
  'model.section.useCases': 'Use cases',

  // "Create a model" process block at the top of the page (ADR-0028).
  'model.process.title': 'Create a model',
  'model.process.intro':
    'From objective to deployed model, in six steps. It all starts from a project objective: what you want to detect, on which data, for which hardware.',
  'model.process.flowLabel': 'Steps to create a model',
  'model.process.step.objective': 'Objective',
  'model.process.step.data': 'Data',
  'model.process.step.baseModel': 'Base model',
  'model.process.step.train': 'Train',
  'model.process.step.evaluate': 'Evaluate',
  'model.process.step.deploy': 'Deploy',
  'model.process.cta': 'Start a project objective',
  'model.process.ctaNote': 'We begin at step 1: define a project’s objective.',

  // "Start a project objective" modal.
  'model.startObjective.title': 'Start a project objective',
  'model.startObjective.intro':
    'An objective is defined on a project. Pick an existing project or create one — you’ll then define what the model should detect.',
  'model.startObjective.projectLegend': 'Project',
  'model.startObjective.modeExisting': 'An existing project',
  'model.startObjective.modeNew': 'A new project',
  'model.startObjective.pickProject': 'Project',
  'model.startObjective.newName': 'Project name',
  'model.startObjective.confirm': 'Continue',
  'model.startObjective.noRightTitle': 'No accessible project',
  'model.startObjective.noRightBody':
    'There is no project yet and your role cannot create one. Ask an organization editor to create one.',
  'model.startObjective.useCasesTitle': 'Use cases — to frame the objective',
  'model.startObjective.useCasesHint':
    'Common operational situations, to inspire you before defining the objective.',

  // "Your models" section — empty state.
  'model.yours.empty.title': 'No models yet',
  'model.yours.empty.body':
    'Import a detection model (ONNX) to evaluate it on your annotated data.',
  'model.yours.empty.cta': 'Create a model',

  // "Use cases" section.
  'model.useCases.intro':
    'The operational situations that call for a model. Start from the catalog, or add your own.',
  'model.useCases.dataLabel': 'Data',
  'model.useCases.add': 'Suggest a use case',
  'model.useCases.addSoon':
    'Coming soon: add your own use cases, after checking they do not already exist.',
  'model.useCase.fpv.name': 'FPV detection',
  'model.useCase.fpv.desc': 'Spot FPV drones (immersive piloting), often fast and small.',
  'model.useCase.shahed.name': 'Shahed detection',
  'model.useCase.shahed.desc':
    'Identify Shahed-136 type drones (loitering munitions) at a distance.',
  'model.useCase.lightVehicle.name': 'Light vehicle detection',
  'model.useCase.lightVehicle.desc': 'Spot cars, pickups and vans from an aerial view.',
  'model.useCase.objectTracking.name': 'Object tracking',
  'model.useCase.objectTracking.desc': 'Track a target frame after frame once detected.',
  'model.useCase.terrain.name': 'Terrain reconnaissance',
  'model.useCase.terrain.desc':
    'Characterize the ground: vegetation, buildings, networks, crossing points.',
  'model.useCase.personnel.name': 'Personnel detection',
  'model.useCase.personnel.desc': 'Spot people in open areas, day or night (IR).',

  // "Models" section — pre-trained library.
  'model.lib.intro':
    'A library of pre-trained models to fetch from HuggingFace. Sorted by data type, size and detected objects.',
  'model.lib.filter.all': 'All',
  'model.lib.objectsLabel': 'Objects',
  'model.lib.view': 'View on HuggingFace',
  'model.lib.searchHint': 'Query this library from the search bar or the assistant (coming soon).',
  'model.lib.empty': 'No models for this data type.',
  'model.dataType.optical': 'Optical',
  'model.dataType.ir': 'IR',
  'model.dataType.acoustic': 'Acoustic',
  'model.dataType.radar': 'Radar',
  'model.lib.objects.coco': '80 COCO classes (people, vehicles, animals…)',
  'model.lib.objects.aerial': 'Objects seen from above: pedestrians, cars, trucks, buses, bikes.',
  'model.lib.objects.thermalPerson': 'People and vehicles in thermal imagery.',
  'model.lib.objects.flir': 'Pedestrians, vehicles and two-wheelers (thermal, ADAS).',
  'model.lib.objects.acousticDrone': 'Acoustic drone signature (presence/absence).',
  'model.lib.objects.sarVehicle': 'Vehicles in synthetic-aperture radar imagery.',
  'model.lib.objects.microDoppler': 'Drones vs birds by micro-Doppler signature.',

  'model.lib.loadError': 'Unable to load the library.',
  'model.lib.liveOff': 'Live enrichment disabled — curated catalog.',
  'model.lib.detectsLabel': 'Detects',
  'model.lib.reliability.high': 'Reliability high',
  'model.lib.reliability.medium': 'Reliability medium',
  'model.lib.reliability.low': 'Reliability low',
  'model.lib.reliability.unevaluated': 'To evaluate',
  'model.lib.reliabilityHint.measured': 'Reliability measured on your annotated data.',
  'model.lib.reliabilityHint.unevaluated': 'Not yet evaluated on your data — run an evaluation.',
  'model.lib.field.data': 'Data',
  'model.lib.field.form': 'Form factor',
  'model.lib.field.speed': 'Speed',
  'model.lib.field.ready': 'Ready to use',
  'model.lib.form.embedded': 'Light, embeddable',
  'model.lib.form.ground': 'Ground station',
  'model.lib.speed.realtime': 'Real time',
  'model.lib.speed.batch': 'Batch',
  'model.lib.ready.yes': 'ONNX available',
  'model.lib.ready.no': 'Conversion required',
  'model.lib.rightsLabel': 'Rights',
  'model.lib.rights.commercialYes': 'Commercial use',
  'model.lib.rights.commercialEnterprise': 'Commercial use — Enterprise license required',
  'model.lib.rights.commercialNo': 'Commercial use prohibited',
  'model.lib.rights.militaryYes': 'Military use allowed',
  'model.lib.rights.militaryNo': 'Military use restricted',
  'model.lib.rights.copyleft': 'Contribute as open source (AGPL)',
  'model.lib.rights.attribution': 'Attribution required',
  'model.lib.rights.none': 'No open-source obligation',
  'model.lib.tech.title': 'Technical detail',
  'model.lib.tech.params': 'Parameters',
  'model.lib.tech.license': 'License',
  'model.lib.tech.downloads': 'Downloads',
  'model.lib.tech.updated': 'Updated',
  'model.lib.tech.reported': 'Declared license',
  'model.lib.tech.source': 'Source',

  'data.status.pending': 'Pending',
  'data.status.uploading': 'Uploading',
  'data.status.uploaded': 'Uploaded',
  'data.status.processing': 'Processing',
  'data.status.ready': 'Ready',
  'data.status.failed': 'Failed',
  'data.title': 'Data',
  'data.subtitle': 'Your videos, images and simulations — data collection and preparation.',
  'data.section.videos': 'Videos',
  'data.section.images': 'Images',
  'data.section.simulations': 'Simulations',
  'data.more.videos': 'Other videos',
  'data.more.images': 'Other images',
  'data.more.hint': 'See all',
  'data.videosPage.subtitle': 'Every video in your organization.',
  'data.imagesPage.subtitle': 'Every image extracted in your organization.',
  'data.upload.dropLabel': 'Drag a video here or click to browse — MP4, MOV, MKV (20 GB max)',
  'data.upload.finalizing': 'Finalizing…',
  'data.upload.uploading': 'Uploading',
  'data.action.cancel': 'Cancel',
  'data.upload.errorTitle': 'Upload failed',
  'data.load.errorTitle': 'Unable to load',
  'data.empty': 'No videos yet.',
  'data.emptyInSession': 'No videos in this flight session.',
  'data.meta.file': 'File',
  'data.meta.size': 'Size',
  'data.meta.added': 'Added',
  'data.meta.asset': 'Aircraft',
  'data.action.moveToSession': 'Move to a session',
  'data.action.addToProject': 'Add to project',
  'data.addToProject.heading': 'Add to a project',
  'data.addToProject.empty': 'No projects yet — create one from the Overview.',
  'data.addToProject.add': 'Add',
  'data.addToProject.added': 'Added',
  'project.notFound.title': 'Project not found',
  'project.notFound.body': 'This project does not exist or is not part of your organization.',
  'project.empty': 'This project is empty — add videos, images or models from “Data” and “Model”.',
  'project.videos.empty': 'No videos in this project — add some from “Data”.',
  'project.videos.addedOn': 'Added on',
  'project.videos.remove': 'Remove from project',
  'project.item.remove': 'Remove from project',
  'project.images.of': 'Image from',

  // Project objective & guided plan (ADR-0028).
  'objective.title': 'Objective',
  'objective.empty':
    'No objective yet. Say what you want to build — the platform maps out the plan.',
  'objective.define': 'Define an objective',
  'objective.edit': 'Edit',
  'objective.remove': 'Remove',
  'objective.plan.title': 'Guided action plan',
  'objective.form.task': 'Task',
  'objective.form.targets': 'Targets to detect',
  'objective.form.targetsHint': 'Target classes, comma-separated.',
  'objective.form.targetsPlaceholder': 'e.g. Shahed-136, FPV, light vehicle',
  'objective.form.modality': 'Data modality',
  'objective.form.support': 'Support',
  'objective.form.deployTarget': 'Deployment target',
  'objective.form.dataOnHand': 'Data already on hand',
  'objective.task.DETECTION': 'Detection',
  'objective.task.TRACKING': 'Tracking',
  'objective.task.TERRAIN': 'Terrain recognition',
  'objective.task.TRAJECTORY': 'Trajectory prediction',
  'objective.task.soon': 'coming soon',
  'objective.modality.OPTICAL': 'Optical',
  'objective.modality.INFRARED': 'Infrared',
  'objective.modality.ACOUSTIC': 'Acoustic',
  'objective.modality.RADAR': 'Radar',
  'objective.modality.TELEMETRY': 'Telemetry',
  'objective.support.IMAGE': 'Still image',
  'objective.support.VIDEO_STREAM': 'Video stream',
  'objective.support.TIME_SERIES': 'Time series',
  'objective.deploy.DRONE': 'On-board (drone)',
  'objective.deploy.UGV': 'On-board (UGV)',
  'objective.deploy.GROUND': 'Ground',
  'objective.dataOnHand.NONE': 'Nothing yet',
  'objective.dataOnHand.VIDEOS': 'Some videos',
  'objective.dataOnHand.LOGS': 'Flight logs',
  'objective.step.dataset': 'Build an annotated set',
  'objective.step.datasetUnit': 'annotated images',
  'objective.step.baseModel': 'Choose a base model',
  'objective.step.train': 'Train / fine-tune',
  'objective.step.evaluate': 'Evaluate',
  'objective.step.deploy': 'Deploy to hardware',
  'objective.step.analyze': 'Analyse & improve',
  'objective.step.comingSoon': 'Coming soon',
  'objective.branch.annotate': 'Annotate images',
  'objective.branch.reuse': 'Reuse annotated data',
  'objective.branch.clean': 'Clean a dataset',
  'objective.branch.simulate': 'Generate synthetic data',
  'objective.branch.field': 'Design a field acquisition',
  'objective.branch.adopt': 'Adopt from the library',
  'objective.branch.import': 'Import an .onnx',
  'objective.branch.recommended': 'recommended',
  'data.action.share': 'Share',
  'data.action.delete': 'Delete',
  'data.sessions.title': 'Flight sessions',
  'data.sessions.new': 'New session',
  'data.filter.all': 'All',
  'data.filter.asset': 'Aircraft',
  'data.filter.allAssets': 'All aircraft',
  'data.sessions.edit': 'Edit session',
  'data.error.saveFailed': 'Unable to save.',
  'data.session.editHeading': 'Edit flight session',
  'data.session.newHeading': 'New flight session',
  'data.action.saving': 'Saving…',
  'data.action.save': 'Save',
  'data.session.hint':
    'A flight session groups the videos from one outing: an afternoon, a location, a campaign.',
  'data.field.name': 'Name',
  'data.session.namePlaceholder': 'e.g. MV line — north sector',
  'data.field.campaign': 'Campaign',
  'data.session.campaignHelper': 'The campaign (project) that groups this session and its images.',
  'data.field.description': 'Description',
  'data.field.location': 'Location',
  'data.session.locationPlaceholder': 'e.g. Plot B12, Aubagne',
  'data.field.flightDate': 'Flight date',
  'data.field.contributors': 'Contributors',
  'data.session.contributorsHelper': 'Comma-separated',
  'data.session.contributorsPlaceholder': 'e.g. Alice Martin, Bob Durand',
  'data.field.asset': 'Aircraft',
  'data.session.assetHelper': 'The fleet drone or UGV that flew the session.',
  'data.session.assetNone': '— None —',
  'data.error.title': 'Failed',
  'data.error.moveFailed': 'Unable to move.',
  'data.move.heading': 'Move to a session',
  'data.move.moving': 'Moving…',
  'data.action.move': 'Move',
  'data.move.noSessions': 'No flight session available. Create one first.',
  'data.error.deleteFailed': 'Unable to delete.',
  'data.delete.heading': 'Delete video',
  'data.delete.deleting': 'Deleting…',
  'data.delete.confirmPrefix': 'The video',
  'data.delete.confirmSuffix':
    'and its files will be deleted. Existing annotations are kept for traceability. This action is irreversible.',
  'data.error.shareCreateFailed': 'Unable to create the link.',
  'data.share.heading': 'Share video',
  'data.share.done': 'Done',
  'data.share.creating': 'Creating…',
  'data.share.create': 'Create link',
  'data.share.introPrefix': 'A public link will give access to',
  'data.share.introSuffix': 'no account required.',
  'data.share.expiryLabel': 'Expires in (days)',
  'data.share.includeAnnotations': 'Include annotations',
  'data.share.allowDownload': 'Allow downloading the source',
  'data.share.created': 'Link created. Copy it: the token is shown only once.',
  'data.share.linkLabel': 'Share link',
  'data.action.copied': 'Copied',
  'data.action.copy': 'Copy',
  'data.share.validUntil': 'Valid until',
  'data.error.exportFailed': 'Unable to export.',
  'data.images.title': 'Images',
  'data.images.hint':
    'Click an image to select it — or press and hover to select several — then export the batch in YOLO format (.zip).',
  'data.images.toolbarAria': 'Actions on selected images',
  'data.images.selectedCount': 'selected',
  'data.images.noSelection': 'No selection',
  'data.images.selectAll': 'Select all',
  'data.images.deselectAll': 'Deselect all',
  'data.images.preparing': 'Preparing…',
  'data.images.download': 'Download',
  'data.images.inYolo': 'as YOLO',
  'data.images.exportErrorTitle': 'Export failed',
  'data.images.selectImageOf': 'Select the image from',
  'data.projects.title': 'Campaigns',
  'data.projects.new': 'New campaign',
  'data.projects.edit': 'Edit campaign',
  'data.project.editHeading': 'Edit campaign',
  'data.project.newHeading': 'New campaign',
  'data.project.hint':
    'A campaign (project) groups flight sessions and their images — e.g. “MV network inspection 2026”.',
  'data.project.namePlaceholder': 'e.g. MV network inspection 2026',

  'sim.title': 'Simulation',
  'sim.lead':
    'Generate synthetic training data, matched to the situation, when real data is missing to fine-tune a model.',
  'sim.dormantNote':
    'Photorealistic rendering is an opt-in capability: scenario authoring is open, launching activates with the GPU node.',
  'sim.launchDormantTitle': 'Rendering unavailable',
  'sim.launchDormantBody':
    'The scenario is saved. Rendering will start once the GPU node is enabled (opt-in sovereignty).',
  'sim.startWith': 'Start with',
  'sim.describeToAssistant': 'Describe to the assistant',
  'sim.field.name': 'Batch name',
  'sim.field.namePlaceholder': 'e.g. Field search — cloudy morning',
  'sim.field.environment': 'Environment',
  'sim.field.timeOfDay': 'Time of day',
  'sim.field.sky': 'Sky',
  'sim.field.altitude': 'Altitude (m)',
  'sim.field.gimbal': 'Gimbal pitch (°)',
  'sim.field.imageCount': 'Number of images',
  'sim.field.targets': 'Targets to detect',
  'sim.field.threats': 'Aerial threats',
  'sim.field.outputs': 'Outputs',
  'sim.noThreat': 'No threat — add one from a preset or by hand.',
  'sim.threatPreset.fpvKamikaze': 'FPV kamikaze 10″',
  'sim.threatPreset.swarm': 'Swarm ×10',
  'sim.threatPreset.fixedWing': 'Fixed-wing',
  'sim.threatPreset.blank': 'Blank threat',
  'sim.threat.remove': 'Remove threat',
  'sim.threat.type': 'Type',
  'sim.threat.label': 'Detection label',
  'sim.threat.labelDefault': '= type',
  'sim.threat.size': 'Size (″)',
  'sim.threat.count': 'Count',
  'sim.threat.speedMin': 'Speed min (m/s)',
  'sim.threat.speedMax': 'Speed max (m/s)',
  'sim.threat.approach': 'Approach',
  'sim.threat.trajectory': 'Trajectory',
  'sim.threat.dispersion': 'Dispersion (°)',
  'sim.threatType.fpv': 'FPV',
  'sim.threatType.fixedWing': 'Fixed-wing',
  'sim.threatType.quad': 'Quad',
  'sim.threatType.vtol': 'VTOL',
  'sim.traj.dive': 'Dive',
  'sim.traj.pass': 'Pass',
  'sim.traj.orbit': 'Orbit',
  'sim.traj.disperse': 'Disperse',
  'sim.traj.hover': 'Hover',
  'sim.approach.frontal': 'Frontal',
  'sim.approach.lateral': 'Lateral',
  'sim.approach.vertical': 'Vertical',
  'sim.approach.mixed': 'Multiple',
  'sim.field.envSource': 'Environment source',
  'sim.envSource.preset': 'Preset',
  'sim.envSource.reconstruction': 'Reconstruction — from your media',
  'sim.field.referenceMedia': 'Area media',
  'sim.media.dormant': '3D reconstruction · dormant (GPU)',
  'sim.media.filter': 'Filter by name…',
  'sim.media.all': 'All',
  'sim.media.videos': 'Videos',
  'sim.media.images': 'Images',
  'sim.media.empty': 'No media — add some via Data → Videos / Images.',
  'sim.media.selected': 'media selected',
  'sim.env.field': 'Field',
  'sim.env.forest': 'Forest',
  'sim.env.denseHabitat': 'Dense habitat',
  'sim.env.periurban': 'Peri-urban',
  'sim.time.dawn': 'Dawn',
  'sim.time.day': 'Day',
  'sim.time.dusk': 'Dusk',
  'sim.time.night': 'Night',
  'sim.sky.clear': 'Clear',
  'sim.sky.partlyCloudy': 'Cloudy',
  'sim.sky.overcast': 'Overcast',
  'sim.sky.fog': 'Fog',
  'sim.output.images': 'Labeled images',
  'sim.output.video': 'Video',
  'sim.output.imagesShort': 'images',
  'sim.output.videoShort': 'video',
  'sim.removeTarget': 'Remove target',
  'sim.noTarget': 'No target — add at least one.',
  'sim.addTarget': 'Add',
  'sim.addTargetPlaceholder': 'Label (e.g. person, vehicle)',
  'sim.recap.title': 'Summary',
  'sim.recap.previewSoon': '2.5D preview — coming soon',
  'sim.recap.previewSchematic': 'Schematic 2.5D preview — not the final render',
  'sim.recap.previewPhoto': 'Preview — representative backdrop, not the final render',
  'sim.backdrop.label': 'Backdrop (image base)',
  'sim.backdrop.none': 'schematic (none chosen)',
  'sim.backdrop.choose': 'Choose…',
  'sim.backdrop.remove': 'Remove',
  'sim.backdrop.modalTitle': 'Choose a backdrop for the environment',
  'sim.backdrop.empty': 'No image in the base yet. Extract images from a video.',
  'sim.backdrop.close': 'Close',
  'sim.recap.timeSky': 'Time · sky',
  'sim.recap.altPitch': 'Altitude · pitch',
  'sim.create': 'Create batch',
  'sim.launch': 'Launch render',
  'sim.delete': 'Delete',
  'sim.batches': 'Simulation batches',
  'sim.noBatch': 'No simulation batch yet.',
  'sim.synthetic': 'Synthetic',
  'sim.status.draft': 'Draft',
  'sim.status.queued': 'Queued',
  'sim.status.rendering': 'Rendering…',
  'sim.status.ready': 'To review',
  'sim.status.approved': 'Approved',
  'sim.status.rejected': 'Rejected',
  'sim.status.failed': 'Failed',
  'sim.cancel': 'Cancel',
  'sim.review.approve': 'Approve',
  'sim.review.reject': 'Reject',
  'sim.review.approveError': 'Could not approve.',
  'sim.review.rejectError': 'Could not reject.',
  'sim.review.approvedBy': 'Approved by',
  'sim.review.rejectedBy': 'Rejected by',
  'sim.review.rejectHeading': 'Reject this batch?',
  'sim.review.rejecting': 'Rejecting…',
  'sim.review.reasonLabel': 'Rejection reason',
  'sim.review.reasonPlaceholder': 'e.g. blurry render, mislocated targets, unrealistic lighting…',
  'sim.assistantOff': 'Assistant unavailable: enable it server-side (ADR-0016).',
  'sim.assistantHint':
    'Describe the scene; the assistant proposes a scenario — using your real labels — and fills the form. Then adjust.',
  'sim.assistError': 'Could not propose a scenario; rephrase your description.',
  'sim.assistantPreamble':
    'Propose a simulation scenario (environment, time of day, sky, altitude, targets) for this need:',
  'sim.assistantPlaceholder': 'e.g. detect people fallen in a field at dawn',
  'sim.assistantSend': 'Send',
  'sim.template.fieldSearch': 'Field search',
  'sim.template.forestInspection': 'Forest inspection',
  'sim.template.periurbanNight': 'Peri-urban watch',
  'sim.template.counterDrone': 'Counter-drone defense',

  // --- Flotte de matériels (ADR-0029) ---
  'fleet.title': 'Hardware fleet',
  'fleet.lead':
    'The drones, ground robots and compute units your models are trained on and deployed to. Each record separates compute and sensors — what adapts the model — from airframe metadata.',
  'fleet.add': 'Add hardware',
  'fleet.manage': 'Manage in Deploy →',
  'fleet.empty': 'No hardware in the fleet yet.',
  'fleet.card.componentsUnit': 'components',
  'fleet.kind.DRONE': 'Drone',
  'fleet.kind.UGV': 'Ground robot',
  'fleet.kind.COMPUTE': 'Compute unit',
  'fleet.kind.SENSOR': 'Sensor',
  'fleet.kind.OTHER': 'Other',
  'fleet.status.ACTIVE': 'In service',
  'fleet.status.BENCH': 'On the bench',
  'fleet.status.MAINTENANCE': 'Maintenance',
  'fleet.status.STOCK': 'In stock',
  'fleet.family.COMPUTE': 'Compute',
  'fleet.family.SENSOR': 'Sensor',
  'fleet.family.AIRFRAME': 'Airframe',
  'fleet.save': 'Save',
  'fleet.cancel': 'Cancel',
  'fleet.modal.createTitle': 'Add hardware',
  'fleet.modal.editTitle': 'Hardware',
  'fleet.modal.create': 'Create hardware',
  'fleet.field.name': 'Name',
  'fleet.field.nameInvalid': 'Name is required.',
  'fleet.field.reference': 'Reference',
  'fleet.field.kind': 'Type',
  'fleet.field.status': 'Status',
  'fleet.field.modality': 'Modality',
  'fleet.field.unset': '—',
  'fleet.field.family': 'Family',
  'fleet.field.componentName': 'Component',
  'fleet.field.componentDetail': 'Detail',
  'fleet.field.soc': 'SoC / host',
  'fleet.field.accelerator': 'Accelerator',
  'fleet.field.tops': 'Compute budget',
  'fleet.field.latencyMs': 'Target latency',
  'fleet.field.sensorFeed': 'Sensor feed',
  'fleet.field.inputResolution': 'Input resolution',
  'fleet.field.fovDeg': 'Field of view',
  'fleet.field.massG': 'Mass',
  'fleet.field.enduranceMin': 'Endurance',
  'fleet.field.propulsion': 'Propulsion',
  'fleet.field.maxSpeedMs': 'Max speed',
  'fleet.field.registration': 'Registration',
  'fleet.field.homeBase': 'Base / storage',
  'fleet.unit.tops': 'TOPS',
  'fleet.unit.ms': 'ms',
  'fleet.unit.deg': '°',
  'fleet.unit.g': 'g',
  'fleet.unit.min': 'min',
  'fleet.unit.ms2': 'm/s',
  'fleet.fam.model.badge': 'Adapts the model',
  'fleet.fam.model.title': 'Compute & sensor',
  'fleet.fam.model.sub':
    'The compute budget and sensors that constrain the model’s architecture, input resolution and export format.',
  'fleet.fam.meta.badge': 'Inventory',
  'fleet.fam.meta.title': 'Airframe & metadata',
  'fleet.fam.meta.sub':
    'Useful for field planning and fleet tracking. Never a parameter of the model.',
  'fleet.components.title': 'Components',
  'fleet.components.add': 'Add a component',
  'fleet.components.remove': 'Remove',
  'fleet.wiring.note':
    'In time, this hardware will be the deployment target of an objective, feed deployment and close the post-deployment analysis loop.',
  'fleet.delete.trigger': 'Remove this hardware',
  'fleet.delete.confirm': 'Permanently remove this hardware?',
  'fleet.delete.yes': 'Remove',

  // --- Bulles d'aide (HelpHint) ---
  'help.label': 'Help',
  'help.overview.projects':
    'A project groups videos, images and models cross-cuttingly around one goal — independent of collection campaigns.',
  'help.overview.fleet':
    'The fleet lists drones, robots and compute units. Each record separates compute and sensors (what adapts the model) from airframe metadata.',
  'help.overview.evaluatedModels':
    "mAP (mean Average Precision) measures a detection model's quality: the higher it is, the better the model locates and classifies objects.",
  'help.data.videos':
    'Upload your drone videos, then annotate them. The bytes go straight to secure storage, never through a middleman.',
  'help.data.images':
    'Extract still frames from videos to annotate with bounding boxes — the basis of a detection training set.',
  'help.data.campaigns':
    'A campaign groups flight sessions (field outings). It organises collection, distinct from cross-cutting projects.',
  'help.data.addToProject':
    'Attach the selection to a cross-cutting project to bring it together with other media and models around one goal.',
  'help.model.import':
    'Import your own ONNX weights. The model is evaluated on your data before any deployment. To start from an existing model, adopt one from the Model Zoo catalog.',
  'help.model.registry':
    'Your models and their versions (top of the Model Zoo). The mAP (detection quality) lets you compare them; only a ready, evaluated model can be deployed.',
  'help.model.catalog':
    'Pre-trained models (Yolo, ResNet…) ready to adopt, then evaluate on your data. Each card shows reliability, usage rights and license.',
  'help.model.process':
    'The full path to a model: define a project’s objective (what to detect, on which data, for which hardware), then data, base model, training, evaluation and deployment. Progress is read from your real data.',
  'help.members.roles':
    'Roles range from Viewer (read-only) to Owner, through Annotator, Editor and Admin. Each role inherits the rights of the ones before it.',
  'help.members.invite':
    'An invitation sends a single-use link; the person joins the organisation with the chosen role. A pending invitation can be revoked.',
  'help.simulation.what':
    'Generate synthetic training images (scenes, threats, weather). Each batch is reviewed by a human before it can feed training.',
  'help.objective':
    'The objective states what the project aims to detect and under what conditions. It derives a guided plan computed from the real data state — never a frozen timestamp.',

  // --- Guided tour (ADR-0031): engine controls ---
  'tour.step': 'Step',
  'tour.next': 'Next',
  'tour.prev': 'Back',
  'tour.skip': 'Skip',
  'tour.finish': 'Done',

  // --- "overview" tour: the four sections + help ---
  'tour.overview.overview.title': 'Overview — your dashboard',
  'tour.overview.overview.text':
    'The starting point: latest videos, evaluated models and fleet status, at a glance.',
  'tour.overview.data.title': 'Data — your data',
  'tour.overview.data.text':
    'Upload your drone videos and extract frames to annotate. Bytes go straight to secure storage, never through a middleman.',
  'tour.overview.model.title': 'Model — your models',
  'tour.overview.model.text':
    'Import, adopt from the library, or train detection models, then track their evaluation metrics.',
  'tour.overview.deploy.title': 'Deploy — your fleet',
  'tour.overview.deploy.text':
    'List drones, robots and compute units, and compose their part builds. Mostly for the fleet manager — but always accessible.',
  'tour.overview.account.title': 'Your account and settings',
  'tour.overview.account.text':
    'Profile, theme, sign out — and your usage profile, which you can change anytime. You can also replay this tour.',

  // --- Personas (ADR-0031): first-login modal + profile setting ---
  'persona.modal.title': 'How do you use the platform?',
  'persona.modal.subtitle':
    "Your profile tailors your home tab and the guided tours you're offered. Nothing is hidden, and you can change it anytime in your profile. It's a display preference — not a role or permission.",
  'persona.modal.confirm': 'Continue',
  'persona.modal.skip': 'Skip — stay a Generalist',
  'persona.GENERALIST.label': 'Generalist',
  'persona.GENERALIST.desc': 'Getting started and all-round use of the platform.',
  'persona.GENERALIST.lands': 'Opens: Overview',
  'persona.COLLECTOR.label': 'Field collector',
  'persona.COLLECTOR.desc': 'I upload and organise my flight videos and images.',
  'persona.COLLECTOR.lands': 'Opens: Data',
  'persona.AI_ENGINEER.label': 'AI engineer',
  'persona.AI_ENGINEER.desc': 'I train, import and evaluate detection models.',
  'persona.AI_ENGINEER.lands': 'Opens: Model',
  'persona.FLEET_MANAGER.label': 'Fleet manager',
  'persona.FLEET_MANAGER.desc': 'I manage field hardware and their part builds.',
  'persona.FLEET_MANAGER.lands': 'Opens: Deploy',
  'persona.OPERATOR.label': 'Field operator',
  'persona.OPERATOR.desc': 'I defend a zone: I watch incoming threats and my defender drones.',
  'persona.OPERATOR.lands': 'Opens: Ops',

  // --- Operational counter-UAS view (ADR-0040) ---
  'ops.title': 'Operational picture',
  'ops.subtitle':
    'Detected threats and protective assets over the defended zone, in near real time.',
  'ops.zone.label': 'Defended zone',
  'ops.zone.loading': 'Loading zones…',
  'ops.zone.none': 'No operational zone',
  'ops.zone.empty': 'No zone defined yet. An administrator creates one via the operations API.',
  'ops.picture.loading': 'Loading the picture…',
  'ops.picture.error': 'The picture could not be loaded.',
  'ops.live': 'Live',
  'ops.updated': 'Updated',
  'ops.basemap.missing':
    'No basemap configured: positions shown over a neutral (sovereign) background.',
  'ops.threats.title': 'Threats',
  'ops.threats.none': 'No active threat',
  'ops.defenders.title': 'Defenders',
  'ops.defenders.none': 'No defender positioned',
  'ops.legend.title': 'Legend',
  'ops.field.speed': 'Speed',
  'ops.field.heading': 'Heading',
  'ops.field.altitude': 'Altitude',
  'ops.field.size': 'Size',
  'ops.field.battery': 'Battery',
  'ops.field.lastSeen': 'Seen',
  'ops.level.LOW': 'Low',
  'ops.level.MEDIUM': 'Medium',
  'ops.level.HIGH': 'High',
  'ops.level.CRITICAL': 'Critical',
  'ops.class.UNKNOWN': 'Unknown',
  'ops.class.MULTIROTOR': 'Multirotor',
  'ops.class.FIXED_WING': 'Fixed wing',
  'ops.class.HELICOPTER': 'Helicopter',
  'ops.class.OTHER': 'Other',
  'ops.status.AVAILABLE': 'Available',
  'ops.status.ENGAGED': 'Engaged',
  'ops.status.RETURNING': 'Returning',
  'ops.status.CHARGING': 'Charging',
  'ops.status.OFFLINE': 'Offline',
  'ops.manage.add': 'Attach',
  'ops.manage.assetLabel': 'Asset to attach',
  'ops.manage.noCandidates': 'No drone/UGV available to attach.',
  'ops.manage.status': 'Defender status',
  'ops.manage.reposition': 'Reposition',
  'ops.manage.remove': 'Remove',
  'ops.manage.placing': 'Click the map to position',
  'ops.manage.cancelPlacing': 'Cancel',
  'ops.manage.error': 'The action failed.',
  'ops.zone.new': 'New zone',
  'ops.zone.create.title': 'Create a defended zone',
  'ops.zone.create.name': 'Zone name',
  'ops.zone.create.lat': 'Center latitude',
  'ops.zone.create.lon': 'Center longitude',
  'ops.zone.create.radius': 'Defended radius (m)',
  'ops.zone.create.submit': 'Create zone',
  'ops.zone.create.cancel': 'Cancel',
  'ops.sim.label': 'Simulated threats',
  'ops.sim.on': 'On',
  'ops.sim.off': 'Off',

  // --- Section tours (ADR-0031, increment 2): replay + per-page content ---
  'tour.replay': 'Replay tour',
  'tour.data.videos.title': 'Your videos',
  'tour.data.videos.text':
    'Upload your drone videos and track their status. From a ready video you can annotate and extract frames.',
  'tour.data.images.title': 'Extracted frames',
  'tour.data.images.text':
    'Still frames pulled from videos gather here: the material to annotate with bounding boxes for training.',
  'tour.model.import.title': 'Import or adopt a model',
  'tour.model.import.text':
    'Start from a model in the curated library, or import your own ONNX weights to evaluate on your data.',
  'tour.model.registry.title': 'Model registry',
  'tour.model.registry.text':
    'Your models and their versions, with evaluation metrics (mAP) — enough to compare and choose which to deploy.',
  'tour.deploy.compute.title': 'On-board compute',
  'tour.deploy.compute.text':
    'A hardware item’s compute unit and sensors: what determines which model can run on it, and how it is adapted.',
  'tour.deploy.hardware.title': 'Hardware — catalogue & build',
  'tour.deploy.hardware.text':
    'Browse the organised parts catalogue and compose each fleet device’s build.',
  // --- Hardware : catalogue de pièces + nomenclature (ADR-0030, incrément 1c) ---
  // --- Packaging de modèle : déploiement edge (ADR-0034) ---
  'deploy.packaging.title': 'Packaging',
  'deploy.packaging.lead':
    'Package a validated model for a device’s target runtime (ONNX, TFLite, TensorRT), with the quantization matched to its compute budget.',
  'deploy.packaging.dormantNote':
    'Drafting a package is possible; the build is dormant until the conversion node is enabled.',
  'deploy.packaging.dormantTitle': 'Build unavailable',
  'deploy.packaging.dormantBody':
    'Packaging is dormant: conversion/quantization will run on an external node, enabled later.',
  'help.deploy.packaging':
    'A package freezes the packaging choices (runtime, quantization, input size) of a validated model for a hardware target. It is the target’s compute envelope — not its airframe — that recommends the format and quantization.',
  'deploy.packaging.field.model': 'Validated model',
  'deploy.packaging.field.modelPlaceholder': 'Choose a model…',
  'deploy.packaging.field.target': 'Hardware target',
  'deploy.packaging.field.targetNone': 'No target (generic)',
  'deploy.packaging.field.runtime': 'Runtime',
  'deploy.packaging.field.quantization': 'Quantization',
  'deploy.packaging.field.inputSize': 'Input size',
  'deploy.packaging.noReadyModel': 'No validated (READY) model to package yet.',
  'deploy.packaging.computeEnvelope': 'Target compute envelope',
  'deploy.packaging.computeEmpty': 'No compute envelope specified',
  'deploy.packaging.recommendation': 'Recommended',
  'deploy.packaging.create': 'Create package',
  'deploy.packaging.createError': 'Could not create.',
  'deploy.packaging.buildError': 'Could not launch.',
  'deploy.packaging.downloadError': 'Could not download.',
  'deploy.packaging.packages': 'Packages',
  'deploy.packaging.empty': 'No packages yet.',
  'deploy.packaging.build': 'Build',
  'deploy.packaging.download': 'Download binary',
  'deploy.packaging.delete': 'Delete',
  'deploy.packaging.runtime.onnx': 'ONNX',
  'deploy.packaging.runtime.tflite': 'TFLite',
  'deploy.packaging.runtime.tensorrt': 'TensorRT',
  'deploy.packaging.quant.none': 'None (FP32)',
  'deploy.packaging.quant.fp16': 'FP16',
  'deploy.packaging.quant.int8': 'INT8',
  'deploy.packaging.status.draft': 'Draft',
  'deploy.packaging.status.queued': 'Queued',
  'deploy.packaging.status.building': 'Building',
  'deploy.packaging.status.ready': 'Ready',
  'deploy.packaging.status.failed': 'Failed',
  'deploy.hardwareLabel': 'Hardware',
  'deploy.hardwareText':
    'On-board hardware: compose each device’s part build from the organised parts catalogue.',
  'help.deploy.hardware':
    'The catalogue lists reference FPV parts (frames, motors, ESCs, cameras…). A build lists a given device’s parts, each pointing to an official catalogue reference.',
  'hw.tab.catalog': 'Catalogue',
  'hw.tab.build': 'Build',
  'hw.catalog.searchLabel': 'Search a part',
  'hw.catalog.searchPlaceholder': 'brand, model…',
  'hw.catalog.all': 'All',
  'hw.catalog.curated':
    'Global organised catalogue: readable by everyone; adding and deprecating references is reserved to the SUPERADMIN role. No duplicates (unique identity category·brand·model·variant).',
  'hw.catalog.empty': 'No reference for this filter.',
  'hw.photos.title': 'Build photos',
  'hw.photos.add': 'Add photos',
  'hw.photos.uploading': 'Uploading…',
  'hw.photos.hint': 'From your browser — JPEG, PNG or WebP, 8 MB max. Bytes never transit the API.',
  'hw.photos.remove': 'Remove',
  'hw.photos.badType': 'Unsupported format (JPEG, PNG or WebP).',
  'hw.photos.tooBig': 'Photo too large (max 8 MB).',
  'hw.photos.uploadFailed': 'Upload failed.',
  'hw.media.edit': 'Edit image',
  'hw.media.add': 'Add image',
  'hw.media.title': 'Part illustration',
  'hw.media.close': 'Close',
  'hw.media.importLabel': 'Import from manufacturer URL',
  'hw.media.importPlaceholder': 'https://manufacturer.com/…/product.jpg',
  'hw.media.import': 'Import',
  'hw.media.or': 'or',
  'hw.media.upload': 'Upload a file',
  'hw.media.remove': 'Remove image',
  'hw.media.hint': 'Allowed manufacturer hosts only. JPEG/PNG/WebP, 5 MB max.',
  'hw.media.working': 'Working…',
  'hw.media.needUrl': 'Paste a manufacturer URL.',
  'hw.media.badType': 'Unsupported format (JPEG, PNG or WebP).',
  'hw.media.tooBig': 'Image too large (max 5 MB).',
  'hw.media.uploadFailed': 'Upload failed.',
  'hw.status.active': 'Active',
  'hw.status.deprecated': 'Deprecated',
  'hw.cat.FRAME': 'Frame',
  'hw.cat.MOTOR': 'Motors',
  'hw.cat.ESC': 'ESC',
  'hw.cat.FC': 'Flight controller',
  'hw.cat.CAMERA': 'Cameras',
  'hw.cat.VTX': 'Video transmitter',
  'hw.cat.RX': 'Receiver',
  'hw.cat.BATTERY': 'Battery',
  'hw.cat.PROP': 'Propellers',
  'hw.cat.ANTENNA': 'Antennas',
  'hw.cat.GPS': 'GPS',
  'hw.cat.OTHER': 'Other',
  'hw.build.assetLabel': 'Device',
  'hw.build.addPart': 'Add a part',
  'hw.build.save': 'Save',
  'hw.build.saved': 'Build saved',
  'hw.build.saveError': 'Could not save.',
  'hw.build.empty': 'No parts. “Add a part” to compose the build.',
  'hw.build.note':
    'Each line references an official catalogue part. Organisation-tuned variants (attached to an official) come in a later increment.',
  'hw.build.summary': 'Summary',
  'hw.build.lines': 'Lines',
  'hw.build.totalQty': 'Parts (total qty)',
  'hw.build.coverage': 'Coverage by category',
  'hw.build.col.slot': 'Slot',
  'hw.build.col.part': 'Part',
  'hw.build.col.qty': 'Qty',
  'hw.build.qtyMinus': 'Decrease quantity',
  'hw.build.qtyPlus': 'Increase quantity',
  'hw.build.remove': 'Remove line',
  'hw.build.pickHeading': 'Add a part',
  'hw.build.pickAdd': 'Add',
  'hw.build.noAsset.title': 'No hardware in the fleet',
  'hw.build.noAsset.body': 'Add a device to the fleet first to compose its build.',
  'hw.build.noAsset.link': 'Go to the fleet',
  // --- Variantes tunées de nomenclature (ADR-0030 §3, incrément 1d) ---
  'hw.build.variantLabel': 'Variant',
  'hw.build.official': '— (official)',
  'hw.build.newVariant': '+ Create a variant…',
  'hw.build.tuned': 'tuned',
  'hw.build.tunedCount': 'Tuned variants',
  'hw.variant.heading': 'Create a tuned variant',
  'hw.variant.for': 'Derived from',
  'hw.variant.labelField': 'Variant name',
  'hw.variant.labelPlaceholder': 'e.g. Rewind 1750KV',
  'hw.variant.noteField': 'Note (optional)',
  'hw.variant.overrides': 'Spec overrides (optional)',
  'hw.variant.overrideKey': 'key',
  'hw.variant.overrideValue': 'value',
  'hw.variant.addOverride': 'Add an override',
  'hw.variant.removeOverride': 'Remove override',
  'hw.variant.create': 'Create',
  'hw.variant.creating': 'Creating…',
  'hw.variant.cancel': 'Cancel',
  'hw.variant.createError': 'Could not create.',

  'search.open': 'Advanced search',
  'search.back': '← Videos',
  'search.title': 'Advanced search',
  'search.subtitle': 'Filter videos by text, aircraft, pilot, dates, annotations and labels.',
  'search.query': 'Full-text search',
  'search.queryPlaceholder': 'e.g. HV line pylon',
  'search.status': 'Status',
  'search.any': 'Any',
  'search.asset': 'Aircraft',
  'search.anyAsset': 'All aircraft',
  'search.pilot': 'Pilot',
  'search.flownFrom': 'Flown from',
  'search.flownTo': 'Flown to',
  'search.annotations': 'Annotations',
  'search.withAnnotations': 'With annotations',
  'search.withoutAnnotations': 'Without annotations',
  'search.sortBy': 'Sort by',
  'search.sort.createdAt': 'Date added',
  'search.sort.flownAt': 'Capture date',
  'search.sort.title': 'Title',
  'search.sort.duration': 'Duration',
  'search.order': 'Order',
  'search.order.desc': 'Descending',
  'search.order.asc': 'Ascending',
  'search.labels': 'Labels',
  'search.submit': 'Search',
  'search.reset': 'Reset',
  'search.errorTitle': 'Search failed',
  'search.noResults': 'No video matches these criteria.',
  'search.results': 'result(s)',
  'search.loadMore': 'Load more',
  'search.loading': 'Loading…',
  'search.geo.title': 'Geographic area',
  'search.geo.canvasLabel': 'Area selection map; draw a rectangle to filter.',
  'search.geo.hint': 'Draw a rectangle on the map to restrict to an area.',
  'search.geo.emptyHint': 'No geolocated flight tracks yet — you can still draw an area.',
  'search.geo.clear': 'Clear area',
};
